import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin: fire a test email through the configured SMTP provider so
// deliverability (inbox vs spam) can be confirmed anytime.
export default function EmailTestCard({ getAuthHeader }) {
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const send = async () => {
    setSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/email/test`,
        { to: to.trim() || undefined },
        { headers: getAuthHeader() },
      );
      setLastResult(res.data);
      toast.success(`Test email sent to ${res.data.to} via ${res.data.provider || 'configured provider'} — check the inbox (and spam folder)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed — check SMTP settings / backend logs');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="wm-card p-6 rounded-lg border border-border space-y-4 max-w-2xl" data-testid="email-test-card">
      <div>
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Mail className="w-5 h-5 text-gold" /> Email Delivery Test
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Sends a test message through your configured mail server (SMTP) so you can confirm
          transactional emails — login alerts, password resets, member notifications — land in
          the inbox and not spam.
        </p>
      </div>

      <div className="space-y-2">
        <Label className="text-foreground">Send to</Label>
        <Input
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="Leave blank to send to your admin email"
          className="bg-input border-border text-foreground"
          data-testid="email-test-to-input"
        />
      </div>

      <Button
        onClick={send}
        disabled={sending}
        className="bg-primary text-primary-foreground hover:bg-primary/90"
        data-testid="email-test-send-btn"
      >
        {sending ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
        ) : (
          <><Send className="w-4 h-4 mr-2" /> Send test email</>
        )}
      </Button>

      {lastResult && (
        <p className="text-xs text-muted-foreground" data-testid="email-test-result">
          Last test: sent to <span className="text-foreground">{lastResult.to}</span> via{' '}
          <span className="text-foreground">{lastResult.provider || 'log only (no provider configured)'}</span> —
          status <span className="text-foreground">{lastResult.status}</span>. If it isn't in the inbox within a
          minute, check the spam folder and your SPF/DKIM DNS records.
        </p>
      )}
    </div>
  );
}
